<template>
  <div class="modal_wrapper active">
    <div class="modal_window white_box create_report_modal">
      <button
        class="close_modal close_btn"
        @click="closeModal()"
      >
        <svg
          fill="none"
          height="32"
          viewBox="0 0 32 32"
          width="32"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M25 7L7 25"
            stroke="#040707"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
          <path
            d="M25 25L7 7"
            stroke="#040707"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
        </svg>
      </button>

      <h3 class="heading_h4 font_bold mb_16">
        Edit {{ reportModal.type === 'templates' ? 'template' : 'report' }}
      </h3>

      <div class="content_wrap">
        <div :class="'section_wrap '+(settings === 'report' ? 'open' : '')">
          <div
            class="section_head"
            @click="(settings === 'report' ? settings = '' : settings = 'report')"
          >
            <h3 class="section_heading">
              Report Settings
            </h3>
            <div class="section_icons">
              <div
                v-if="reportErrors.length"
                class="errors"
              >
                {{ reportErrors.length }}
              </div>
              <i class="icon arrow_down_icon caret" />
            </div>
          </div>
          <accordion-transition>
            <div
              v-if="settings === 'report'"
              class="section_body"
            >
              <custom-input
                v-model="data.name"
                :label="`${reportModal.type === 'report' ? 'Report' : 'Template'} name`"
                :placeholder="`Please add a name for your ${reportModal.type}`"
                :validation-errors="errors.name"
                width="full"
              />

              <custom-select
                v-model="data.paper_size"
                :options="['A4', 'Letter']"
                :validation-errors="errors.paper_size"
                label="Paper size"
                width="half"
              />

              <custom-select
                v-model="data.orientation"
                :options="{
                  'portrait': 'Portrait',
                  'landscape': 'Landscape'
                }"
                :validation-errors="errors.orientation"
                label="Page orientation"
                width="half"
              />

              <custom-select
                v-model="data.font"
                :options="[
                  { value: 'DM Sans, sans-serif', label: 'DM Sans', font: 'DM Sans, sans-serif'},
                  { value: 'Arial, Helvetica, sans-serif', label: 'Arial', font: 'Arial, Helvetica, sans-serif'},
                  { value: 'Cambria, Cochin, Georgia, Times, Times New Roman, serif', label: 'Cambria', font: 'Cambria, Cochin, Georgia, Times, Times New Roman, serif'},
                  { value: 'Courier New, Courier, monospace', label: 'Courier New', font: 'Courier New, Courier, monospace'},
                  { value: 'Franklin Gothic Medium, Arial Narrow, Arial, sans-serif', label: 'Franklin Gothic Medium', font: 'Franklin Gothic Medium, Arial Narrow, Arial, sans-serif'},
                  { value: 'Lucida Sans, Lucida Sans Regular, Lucida Grande, Lucida Sans Unicode, Geneva, Verdana, sans-serif', label: 'Lucida Sans', font: 'Lucida Sans, Lucida Sans Regular, Lucida Grande, Lucida Sans Unicode, Geneva, Verdana, sans-serif'},
                  { value: 'Roboto, -apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif', label: 'Roboto', font: 'Roboto, -apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif'},
                  { value: 'Times New Roman, Times, serif', label: 'Times New Roman', font: 'Times New Roman, Times, serif'}
                ]"
                :validation-errors="errors.font"
                label="Report font"
                width="half"
              />

              <custom-select
                v-model="data.date_format"
                :options="[
                  'DD/MM/YYYY',
                  'DD-MM-YYYY',
                  'DD.MM.YYYY',
                  'MM/DD/YYYY',
                  'MM-DD-YYYY',
                  'MM.DD.YYYY'
                ]"
                :validation-errors="errors.date_format"
                label="Date format"
                width="half"
              />

              <custom-input
                :disabled="true"
                :placeholder="selectedDateRange"
                :width="data.comp_range ? 'third' : 'half'"
                label="Date range"
              />

              <custom-input
                v-if="data.comp_range"
                :disabled="true"
                :placeholder="selectedCompRange"
                label="Comparison date range"
                width="third"
              />

              <button
                :class="'btn secondary_btn open-datepicker '+(data.comp_range ? 'third' : 'half')"
                @click="datepicker = true"
              >
                <span>Select Date Range</span>
              </button>

              <date-range-picker
                v-if="datepicker"
                v-model:compFromDate="data.comp_from_date"
                v-model:compRange="data.comp_range"
                v-model:compToDate="data.comp_to_date"
                v-model:dateRange="data.date_range"
                v-model:fromDate="data.from_date"
                v-model:toDate="data.to_date"
                @close="datepicker = false"
              />

              <custom-select
                v-if="reportModal.type === 'templates' && canSetScope"
                v-model="data.scope"
                :options="scopes"
                :validation-errors="errors.scope"
                label="Template scope"
                width="full"
              />

              <custom-color-multiselect
                v-model="data.colors"
                :validation-errors="errors.colors"
                label="Report Chart Colors"
                width="full"
              />
            </div>
          </accordion-transition>
        </div>

        <div :class="'section_wrap '+(settings === 'header' ? 'open' : '')">
          <div
            class="section_head"
            @click="(settings === 'header' ? settings = '' : settings = 'header')"
          >
            <h3 class="section_heading">
              Header Settings
            </h3>
            <div class="section_icons">
              <div
                v-if="headerErrors.length"
                class="errors"
              >
                {{ headerErrors.length }}
              </div>
              <i class="icon arrow_down_icon caret" />
            </div>
          </div>
          <accordion-transition>
            <div
              v-if="settings === 'header'"
              class="section_body"
            >
              <div class="logo_preview">
                <img
                  v-if="data.logo_src"
                  :src="data.logo_src"
                  :style="`background: ${data.header_color ?? 'lightgray'};`"
                >
                <div v-else>
                  Please upload a logo
                </div>

                <custom-image-upload
                  v-model="data.logo_src"
                  :label="data.logo_src ? 'Change logo' : 'Upload a logo' "
                  width="full"
                />
              </div>
              <small
                v-if="errors.logo_src"
                class="selection_warning"
              >{{ errors.logo_src ? errors.logo_src[0] : '' }}</small>

              <custom-input
                v-model="data.header_text"
                :validation-errors="errors.header_text"
                label="Header text"
                placeholder="Enter your header text"
              />

              <custom-color-select
                v-model="data.header_color"
                :validation-errors="errors.header_color"
                label="Header color"
                placeholder="Enter a hex value E.g. #ffffff"
                width="half"
              />

              <custom-color-select
                v-model="data.header_text_color"
                :validation-errors="errors.header_text_color"
                label="Header text color"
                placeholder="Enter a hex value E.g. #ffffff"
                width="half"
              />

              <custom-select
                v-model="data.header_font"
                :options="[
                  { value: 'DM Sans, sans-serif', label: 'DM Sans', font: 'DM Sans, sans-serif'},
                  { value: 'Arial, Helvetica, sans-serif', label: 'Arial', font: 'Arial, Helvetica, sans-serif'},
                  { value: 'Cambria, Cochin, Georgia, Times, Times New Roman, serif', label: 'Cambria', font: 'Cambria, Cochin, Georgia, Times, Times New Roman, serif'},
                  { value: 'Courier New, Courier, monospace', label: 'Courier New', font: 'Courier New, Courier, monospace'},
                  { value: 'Franklin Gothic Medium, Arial Narrow, Arial, sans-serif', label: 'Franklin Gothic Medium', font: 'Franklin Gothic Medium, Arial Narrow, Arial, sans-serif'},
                  { value: 'Lucida Sans, Lucida Sans Regular, Lucida Grande, Lucida Sans Unicode, Geneva, Verdana, sans-serif', label: 'Lucida Sans', font: 'Lucida Sans, Lucida Sans Regular, Lucida Grande, Lucida Sans Unicode, Geneva, Verdana, sans-serif'},
                  { value: 'Roboto, -apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif', label: 'Roboto', font: 'Roboto, -apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif'},
                  { value: 'Times New Roman, Times, serif', label: 'Times New Roman', font: 'Times New Roman, Times, serif'}
                ]"
                :validation-errors="errors.header_font"
                label="Header font"
                width="half"
              />

              <custom-toggle
                v-model="data.include_date_range"
                :validation-errors="errors.include_date_range"
                label="Include date range"
                width="half"
              />
            </div>
          </accordion-transition>
        </div>

        <div :class="'section_wrap '+(settings === 'footer' ? 'open' : '')">
          <div
            class="section_head"
            @click="(settings === 'footer' ? settings = '' : settings = 'footer')"
          >
            <h3 class="section_heading">
              Footer Settings
            </h3>
            <div class="section_icons">
              <div
                v-if="footerErrors.length"
                class="errors"
              >
                {{ footerErrors.length }}
              </div>
              <i class="icon arrow_down_icon caret" />
            </div>
          </div>
          <accordion-transition>
            <div
              v-if="settings === 'footer'"
              class="section_body"
            >
              <custom-input
                v-model="data.footer_text"
                :validation-errors="errors.footer_text"
                label="Footer text"
                placeholder="Enter your footer text"
              />

              <custom-color-select
                v-model="data.footer_color"
                :validation-errors="errors.footer_color"
                label="Footer color"
                placeholder="Enter a hex value E.g. #ffffff"
                width="half"
              />

              <custom-color-select
                v-model="data.footer_text_color"
                :validation-errors="errors.footer_text_color"
                label="Footer text color"
                placeholder="Enter a hex value E.g. #ffffff"
                width="half"
              />

              <custom-select
                v-model="data.footer_font"
                :options="[
                  { value: 'DM Sans, sans-serif', label: 'DM Sans', font: 'DM Sans, sans-serif'},
                  { value: 'Arial, Helvetica, sans-serif', label: 'Arial', font: 'Arial, Helvetica, sans-serif'},
                  { value: 'Cambria, Cochin, Georgia, Times, Times New Roman, serif', label: 'Cambria', font: 'Cambria, Cochin, Georgia, Times, Times New Roman, serif'},
                  { value: 'Courier New, Courier, monospace', label: 'Courier New', font: 'Courier New, Courier, monospace'},
                  { value: 'Franklin Gothic Medium, Arial Narrow, Arial, sans-serif', label: 'Franklin Gothic Medium', font: 'Franklin Gothic Medium, Arial Narrow, Arial, sans-serif'},
                  { value: 'Lucida Sans, Lucida Sans Regular, Lucida Grande, Lucida Sans Unicode, Geneva, Verdana, sans-serif', label: 'Lucida Sans', font: 'Lucida Sans, Lucida Sans Regular, Lucida Grande, Lucida Sans Unicode, Geneva, Verdana, sans-serif'},
                  { value: 'Roboto, -apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif', label: 'Roboto', font: 'Roboto, -apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif'},
                  { value: 'Times New Roman, Times, serif', label: 'Times New Roman', font: 'Times New Roman, Times, serif'}
                ]"
                :validation-errors="errors.footer_font"
                label="Footer font"
                width="half"
              />

              <custom-toggle
                v-model="data.include_page_no"
                label="Include page numbers"
                width="half"
              />
            </div>
          </accordion-transition>
        </div>
      </div>


      <div class="mt_24 modal_footer_buttons">
        <button
          class="btn btn_md secondary_btn"
          @click="closeModal()"
        >
          <span class="p_body font_bold">Cancel</span>
        </button>
        <button
          class="btn btn_md primary_btn"
          @click="update()"
        >
          <span class="p_body font_bold">Update</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';
import CustomInput from '../../global/forms/CustomInput.vue';
import CustomSelect from '../../global/forms/CustomSelect.vue';
import CustomColorSelect from '../../global/forms/CustomColorSelect.vue';
import CustomColorMultiselect from '../../global/forms/CustomColorMultiselect.vue';
import CustomImageUpload from '../../global/forms/CustomImageUpload.vue';
import CustomToggle from '../../global/forms/CustomToggle.vue';
import AccordionTransition from '../../global/transitions/AccordionTransition.vue';
import DateRangePicker from '../../global/forms/DateRangePicker.vue';
import {mapActions, mapState} from 'vuex';

export default {
  components: {
    CustomInput,
    CustomSelect,
    CustomColorSelect,
    CustomColorMultiselect,
    CustomImageUpload,
    CustomToggle,
    AccordionTransition,
    DateRangePicker
  },
  emits: ['updated'],
  data() {
    return {
      showFields: '',
      datepicker: false,
      settings: 'report',
      data: {
        name: '',
        from: null,
        id: null,
        scope: '',
        orientation: '',
        paper_size: '',
        font: '',
        colors: [],
        logo_src: '',
        header_color: '',
        header_text: '',
        header_text_color: '',
        header_font: '',
        include_date_range: '',
        date_format: '',
        footer_color: '',
        footer_text: '',
        footer_text_color: '',
        footer_font: '',
        include_page_no: true,
        date_range: '',
        comp_range: null,
        from_date: moment().subtract(8, 'd').format('YYYY-MM-DD'),
        to_date: moment().subtract(1, 'd').format('YYYY-MM-DD'),
        comp_from_date: null,
        comp_to_date: null,
      },
      errors: []
    }
  },
  computed: {
    ...mapState(['reportModal']),
    ...mapState('user', ['user']),
    selectedDateRange() {
      if (this.data.from_date && this.data.to_date) {
        let from = moment(this.data.from_date).format(this.data.date_format);
        let to = moment(this.data.to_date).format(this.data.date_format);
        return from + ' - ' + to;
      }

      return 'Please select dates';
    },
    selectedCompRange() {
      if (this.data.comp_from_date && this.data.comp_to_date) {
        let from = moment(this.data.comp_from_date).format(this.data.date_format);
        let to = moment(this.data.comp_to_date).format(this.data.date_format);
        return from + ' - ' + to;
      }
      return 'Please select dates';
    },
    reportErrors() {
      let fields = ['name', 'paper_size', 'orientation', 'colors'];
      let errors = [];
      _.forEach(this.errors, (error, key) => {
        if (fields.includes(key)) errors.push(key);
      });
      return errors;
    },
    headerErrors() {
      let fields = ['header_color', 'logo_src', 'header_text', 'header_color', 'header_text_color', 'header_font', 'include_date_range'];
      let errors = [];
      _.forEach(this.errors, (error, key) => {
        if (fields.includes(key)) errors.push(key);
      });
      return errors;
    },
    footerErrors() {
      let fields = ['footer_text', 'footer_color', 'footer_text_color', 'footer_font'];
      let errors = [];
      _.forEach(this.errors, (error, key) => {
        if (fields.includes(key)) errors.push(key);
      });
      return errors;
    },
    canSetScope() {
      const {user} = this;
      const organisation = user?.organisation;
      return organisation.organisation_type === 'MULTI'
        || organisation.external_id.startsWith('VEND_')
        || user.user_type === 'ADMIN'
    },
    scopes() {
      const {user} = this;
      const organisation = user?.organisation;
      let scopes = {website: 'Website'};

      if (
        organisation.organisation_type === 'MULTI'
        || organisation.external_id.startsWith('VEND_')
      ) {
        scopes.organisation = 'Organisation';
      }

      if (user.user_type === "ADMIN") {
        scopes.local = 'Local';
      }
      
      return scopes;
    }
  },
  beforeMount() {
    _.merge(this.data, this.reportModal.report);
    this.settings = this.reportModal.section;
    if (this.reportModal.report.organisation_id) {
      this.data.scope = 'organisation';
    } else if (this.reportModal.report.website_id) {
      this.data.scope = 'website';
    } else {
      this.data.scope = 'local';
    }
  },
  methods: {
    ...mapActions('reportModal', ['closeModal']),
    ...mapActions('report', ['saveReport']),
    async update() {
      this.saveReport({type: this.reportModal.type, report: this.data})
        .then(() => {
          this.closeModal();
          this.$emit('updated');
        })
        .catch(response => {
          if (response.status === 422) {
            this.errors = response.data.errors;
          }
        });
    }
  },
}
</script>

<style scoped src="../../../../reports/css/modal.css" />
